import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PushToggle from "../components/PushToggle";
import { Upload, CheckCircle2, Clock, AlertCircle, IdCard, FileText, ArrowLeft, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function ProfileVerification() {
  const { user, ready, setUser } = useAuth();
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(null); // 'id' | 'licence' | 'police_clearance' | null
  const [pccIssuedOn, setPccIssuedOn] = useState("");

  useEffect(() => {
    if (ready && (!user || user === false)) navigate("/login?next=/dashboard/profile");
  }, [user, ready, navigate]);

  useEffect(() => {
    if (user?.id) api.get("/me/documents").then((r) => setDocs(r.data || [])).catch(() => {});
    if (user?.id) api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {});
  }, [user?.id]);

  const LABELS = { id: "ID", licence: "Driver's licence", police_clearance: "Police clearance certificate" };
  const handleUpload = async (file, docType) => {
    if (!file) return;
    if (docType === "police_clearance" && !pccIssuedOn) return toast.error("Enter the certificate's issue date first");
    setUploading(docType);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/me/documents", { doc_type: docType, file_id: up.id, issued_on: docType === "police_clearance" ? pccIssuedOn : undefined });
      const fresh = await api.get("/me/documents");
      setDocs(fresh.data || []);
      toast.success(`${LABELS[docType]} submitted for review`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(null);
    }
  };

  if (!user?.id) return null;

  const idDocs = docs.filter((d) => d.doc_type === "id");
  const licenceDocs = docs.filter((d) => d.doc_type === "licence");
  const pccDocs = docs.filter((d) => d.doc_type === "police_clearance");
  const isDriver = user.role === "driver";
  const pccExpired = user.police_clearance_expires_on && user.police_clearance_expires_on < new Date().toISOString().slice(0, 10);

  return (
    <div className="relative min-h-[calc(100vh-4rem)]">
      <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-10">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm font-semibold text-[#64748B] hover:text-[#0a1f4d]" data-testid="profile-back">
          <ArrowLeft size={14} /> Back to dashboard
        </Link>

        <div className="mt-5">
          <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#64748B]">Verification</p>
          <h1 className="font-brand text-3xl sm:text-4xl text-[#0a1f4d] mt-2">{isDriver ? "Get verified to drive" : "Get verified to book or host"}</h1>
          <p className="text-sm text-[#334155] mt-3 leading-relaxed max-w-xl">
            {isDriver
              ? "Chauffeurs need a clear photo of their ID, driver's licence and a SAPS police clearance certificate issued within the last 6 months. Verification typically takes a few hours during business days."
              : "We need a clear photo of your ID and your driver's licence. Verification typically takes a few hours during business days. Your documents are encrypted and never shared."}
          </p>
        </div>

        <div className="mt-8"><PushToggle /></div>

        <div className={`mt-8 grid grid-cols-1 sm:grid-cols-2 ${isDriver ? "lg:grid-cols-3" : ""} gap-4`}>
          <Stat label="ID document" status={user.id_verified ? "verified" : (idDocs[0]?.status || "missing")} icon={<IdCard size={18} />} />
          <Stat label="Driver's licence" status={user.licence_verified ? "verified" : (licenceDocs[0]?.status || "missing")} icon={<FileText size={18} />} />
          {isDriver && <Stat label="Police clearance" status={pccExpired ? "expired" : user.police_clearance_verified ? "verified" : (pccDocs[0]?.status || "missing")} icon={<ShieldCheck size={18} />} testid="stat-police-clearance" />}
        </div>

        <div className="mt-8 space-y-4">
          <DocumentSection
            title="South African ID"
            description="A clear photo or scan of your ID card or book — both sides if applicable."
            docs={idDocs}
            verified={user.id_verified}
            uploading={uploading === "id"}
            onUpload={(f) => handleUpload(f, "id")}
            testid="upload-id"
          />
          <DocumentSection
            title="Driver's licence"
            description="A clear photo of the front of your valid SA driver's licence."
            docs={licenceDocs}
            verified={user.licence_verified}
            uploading={uploading === "licence"}
            onUpload={(f) => handleUpload(f, "licence")}
            testid="upload-licence"
          />
          {isDriver && (
            <DocumentSection
              title="Police clearance certificate"
              description="A SAPS police clearance certificate issued within the last 6 months (PCC). Required before you can accept chauffeur trips; renew every 6 months."
              docs={pccDocs}
              verified={user.police_clearance_verified && !pccExpired}
              uploading={uploading === "police_clearance"}
              onUpload={(f) => handleUpload(f, "police_clearance")}
              testid="upload-police-clearance"
              extra={
                <div className="mt-4">
                  {pccExpired && <p className="text-xs font-semibold text-[#B91C1C] mb-2" data-testid="pcc-expired">Your certificate expired on {user.police_clearance_expires_on}. Upload a new one to keep accepting trips.</p>}
                  {user.police_clearance_verified && !pccExpired && <p className="text-xs text-[#64748B] mb-2">Valid until {user.police_clearance_expires_on}</p>}
                  <label className="block text-xs tracking-[0.14em] uppercase font-bold text-[#64748B] mb-1.5">Certificate issue date</label>
                  <input type="date" value={pccIssuedOn} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setPccIssuedOn(e.target.value)} className="w-full sm:w-64 bg-[#F7F8FA] border border-[#E2E8F0] rounded-xl px-4 py-2.5 text-sm text-[#0a1f4d] outline-none focus:border-[#1d7ffd]" data-testid="pcc-issued-on" />
                </div>
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, status, icon, testid }) => {
  const map = {
    verified: { bg: "bg-[#10B981]/15", text: "text-[#10B981]", label: "Verified", Icon: CheckCircle2 },
    expired: { bg: "bg-[#EF4444]/15", text: "text-[#B91C1C]", label: "Expired", Icon: AlertCircle },
    pending: { bg: "bg-[#F59E0B]/15", text: "text-[#B45309]", label: "Under review", Icon: Clock },
    approved: { bg: "bg-[#10B981]/15", text: "text-[#10B981]", label: "Verified", Icon: CheckCircle2 },
    rejected: { bg: "bg-[#EF4444]/15", text: "text-[#B91C1C]", label: "Rejected", Icon: AlertCircle },
    missing: { bg: "bg-[#64748B]/10", text: "text-[#475569]", label: "Not submitted", Icon: AlertCircle },
  };
  const s = map[status] || map.missing;
  const Ico = s.Icon;
  return (
    <div className="rounded-2xl border border-[#E2E8F0] p-5 bg-white" data-testid={testid}>
      <div className="text-[#1d7ffd]">{icon}</div>
      <p className="text-[10px] tracking-[0.16em] uppercase font-bold text-[#64748B] mt-3">{label}</p>
      <div className={`inline-flex items-center gap-1 mt-2 ${s.text} text-sm font-bold`}>
        <Ico size={14} /> {s.label}
      </div>
    </div>
  );
};

function DocumentSection({ title, description, docs, verified, uploading, onUpload, testid, extra }) {
  const latest = docs[0];
  return (
    <div className="rounded-2xl border border-[#E2E8F0] p-6 bg-white">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-display text-lg font-bold text-[#0a1f4d]">{title}</h3>
          <p className="text-sm text-[#334155] mt-1">{description}</p>
        </div>
        {verified && (
          <span className="inline-flex items-center gap-1 bg-[#10B981]/15 text-[#10B981] text-xs font-bold px-2.5 py-1 rounded-md">
            <CheckCircle2 size={12} /> Verified
          </span>
        )}
      </div>

      {extra}

      {latest && (
        <div className="mt-4 flex items-center gap-3 p-3 rounded-xl bg-[#F7F8FA] border border-[#E2E8F0]">
          <div className="w-16 h-16 rounded-lg overflow-hidden bg-white flex-shrink-0">
            <img src={`${API}/files/${latest.file_id}`} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[#0a1f4d]">Latest submission</p>
            <p className="text-xs text-[#64748B]">Status: <span className="font-semibold uppercase tracking-wide">{latest.status}</span>{latest.issued_on ? ` · issued ${latest.issued_on}` : ""}</p>
          </div>
        </div>
      )}

      {!verified && (
        <label className="mt-5 block border-2 border-dashed border-[#E2E8F0] rounded-2xl p-6 text-center cursor-pointer hover:border-[#1d7ffd] hover:bg-[#1d7ffd]/5 transition-colors">
          <input type="file" accept="image/*,application/pdf" className="hidden" disabled={uploading} onChange={(e) => onUpload(e.target.files?.[0])} data-testid={testid} />
          <Upload size={20} className="mx-auto text-[#64748B]" />
          <p className="text-sm font-semibold text-[#0a1f4d] mt-2">{uploading ? "Uploading…" : (latest ? "Re-upload" : "Click to upload")}</p>
          <p className="text-xs text-[#64748B] mt-1">JPG, PNG or PDF up to 10 MB</p>
        </label>
      )}
    </div>
  );
}
